import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { bulkIngestService } from "@client/http/bulkIngest.service";
import type {
  IBulkIngestJob,
  BulkIngestJobStatus,
} from "@nendaTypes/NendaTypes";

const STATUS_COLORS: Record<
  BulkIngestJobStatus,
  "default" | "info" | "warning" | "success" | "error"
> = {
  created: "default",
  validated: "info",
  preparing: "warning",
  preparing_failed: "error",
  processing: "warning",
  completed: "success",
  failed: "error",
  cancelling: "warning",
  cancelled: "default",
};

function formatDate(date?: Date | string): string {
  if (!date) return "—";
  return new Date(date).toLocaleString("sv-SE");
}

const BulkIngestListPage: React.FC = () => {
  const navigate = useNavigate();
  const [jobs, setJobs] = useState<IBulkIngestJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newJobName, setNewJobName] = useState("");
  const [newJobDescription, setNewJobDescription] = useState("");

  const fetchJobs = useCallback(() => {
    setLoading(true);
    bulkIngestService.list().subscribe({
      next: (data: IBulkIngestJob[]) => {
        setJobs(data);
        setLoading(false);
      },
      error: () => setLoading(false),
    });
  }, []);

  useEffect(() => {
    fetchJobs();
  }, [fetchJobs]);

  const handleCreate = () => {
    if (!newJobName.trim()) return;
    bulkIngestService
      .create(newJobName.trim(), newJobDescription.trim() || undefined)
      .subscribe({
        next: (job: IBulkIngestJob) => {
          setCreateDialogOpen(false);
          setNewJobName("");
          setNewJobDescription("");
          navigate(String(job._id));
        },
      });
  };

  return (
    <Box sx={{ p: 3 }}>
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 3 }}
      >
        <Typography variant="h5">Bulk Ingest Jobs</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
        >
          New Job
        </Button>
      </Stack>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Items</TableCell>
              <TableCell>Progress</TableCell>
              <TableCell>Created</TableCell>
              <TableCell>Created By</TableCell>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  Loading...
                </TableCell>
              </TableRow>
            )}
            {!loading && jobs.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  No bulk ingest jobs yet
                </TableCell>
              </TableRow>
            )}
            {jobs.map((job) => (
              <TableRow
                key={String(job._id)}
                hover
                sx={{ cursor: "pointer" }}
                onClick={() => navigate(String(job._id))}
              >
                <TableCell>{job.name}</TableCell>
                <TableCell>
                  <Chip
                    label={job.status}
                    color={STATUS_COLORS[job.status]}
                    size="small"
                  />
                </TableCell>
                <TableCell>{job.totalItems}</TableCell>
                <TableCell>
                  {job.completedItems +
                    job.failedItems +
                    (job.cancelledItems ?? 0)}{" "}
                  / {job.totalItems}
                  {job.failedItems > 0 && (
                    <Typography variant="caption" color="error" sx={{ ml: 1 }}>
                      ({job.failedItems} failed)
                    </Typography>
                  )}
                  {(job.cancelledItems ?? 0) > 0 && (
                    <Typography
                      variant="caption"
                      sx={{ ml: 1, color: "grey.600" }}
                    >
                      ({job.cancelledItems} cancelled)
                    </Typography>
                  )}
                </TableCell>
                <TableCell>{formatDate(job.createdAt)}</TableCell>
                <TableCell>{job.createdBy}</TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  {!["preparing", "processing", "cancelling"].includes(
                    job.status
                  ) && (
                    <DeleteIcon
                      fontSize="small"
                      sx={{
                        cursor: "pointer",
                        color: "text.disabled",
                        "&:hover": { color: "error.main" },
                      }}
                      onClick={() => {
                        if (
                          !window.confirm(
                            `Delete job "${job.name}"? This cannot be undone.`
                          )
                        )
                          return;
                        bulkIngestService.remove(String(job._id)).subscribe({
                          next: () => fetchJobs(),
                        });
                      }}
                    />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create Bulk Ingest Job</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Job Name"
              value={newJobName}
              onChange={(e) => setNewJobName(e.target.value)}
              fullWidth
              autoFocus
              placeholder="e.g. Q1 2026 Content Package"
            />
            <TextField
              label="Description (optional)"
              value={newJobDescription}
              onChange={(e) => setNewJobDescription(e.target.value)}
              fullWidth
              multiline
              rows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            disabled={!newJobName.trim()}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BulkIngestListPage;
